/**
 * Breaks up long article bodies without touching the stored HTML.
 * Runs before article-toc.js. Every step only reads patterns the bodies already use:
 *
 * - "Krok 1: ..." H3 sections become a numbered step line.
 * - Paragraphs that open with <strong>Uwaga:</strong>, Wskazówka, Pro tip, Ważne,
 *   Pamiętaj or "Nasza rekomendacja" become a note box.
 * - List items that start with ✅ ❌ ⚠️ get a small drawn icon instead of the emoji.
 * - Table rows marked with ⭐ are highlighted as the recommended option.
 * - Temperature tables get a scale above them; "Tradycyjnie / Próżniowo" tables get bars.
 * - Questions under "Najczęstsze pytania" become collapsible.
 */

(function() {
  const NOTE_KINDS = [
    [/^(nasza rekomendacja|zwrot z inwestycji)/i, 'rec'],
    [/^(wskazówka|pro tip|dodatki)/i, 'tip'],
    [/^(uwaga|ważne|pamiętaj)/i, 'warn'],
  ];

  function notes(content) {
    content.querySelectorAll(':scope > p').forEach((p) => {
      const first = p.firstElementChild;
      if (!first || first.tagName !== 'STRONG' || p.firstChild !== first) return;
      const label = first.textContent.trim();
      if (!label.endsWith(':')) return;
      const kind = NOTE_KINDS.find(([re]) => re.test(label));
      if (!kind) return;
      p.classList.add('ac-note', `ac-note--${kind[1]}`);
      first.classList.add('ac-note__label');
      first.textContent = label.slice(0, -1);
    });
  }

  function iconLists(content) {
    const marks = { '✅': 'ok', '❌': 'no', '⚠️': 'warn', '⚠': 'warn' };
    content.querySelectorAll('li').forEach((li) => {
      const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
      const node = walker.nextNode();
      if (!node) return;
      const text = node.textContent.replace(/^\s+/, '');
      const mark = Object.keys(marks).find((m) => text.startsWith(m));
      if (!mark) return;
      node.textContent = text.slice(mark.length).replace(/^[️\s]+/, '');
      li.classList.add('ac-li', `ac-li--${marks[mark]}`);
      li.parentElement.classList.add('ac-list');
    });
  }

  function steps(content) {
    const heads = [...content.querySelectorAll(':scope > h3')].filter((h) => /^Krok\s+\d+/i.test(h.textContent.trim()));
    heads.forEach((h) => {
      const match = h.textContent.trim().match(/^Krok\s+(\d+)\s*[:.\-]?\s*(.*)$/i);

      let prev = h.previousElementSibling;
      if (prev && prev.tagName === 'HR' && prev.previousElementSibling?.classList.contains('ac-steps')) {
        const hr = prev;
        prev = hr.previousElementSibling;
        hr.remove();
      }
      let group = prev && prev.classList.contains('ac-steps') ? prev : null;
      if (!group) {
        group = document.createElement('div');
        group.className = 'ac-steps';
        h.before(group);
      }

      const step = document.createElement('div');
      step.className = 'ac-step';
      step.dataset.step = match[1];
      let next = h.nextElementSibling;
      h.textContent = match[2] || h.textContent;
      h.classList.add('ac-step__title');
      step.appendChild(h);
      while (next && !/^H[1-3]$/.test(next.tagName) && !next.classList.contains('product-slot') && next.tagName !== 'HR') {
        const after = next.nextElementSibling;
        step.appendChild(next);
        next = after;
      }
      group.appendChild(step);
    });
  }

  function recommendedRows(content) {
    content.querySelectorAll('tr').forEach((tr) => {
      if (!tr.textContent.includes('⭐')) return;
      tr.querySelectorAll('td, th').forEach((cell) => {
        cell.innerHTML = cell.innerHTML.replace(/\s*⭐\s*/g, ' ').trim();
      });
      tr.classList.add('ac-row--rec');
      const first = tr.querySelector('td, th');
      if (first) {
        const badge = document.createElement('span');
        badge.className = 'ac-badge';
        badge.textContent = 'polecane';
        first.appendChild(badge);
      }
    });
  }

  function firstNumber(text) {
    const m = text.replace(',', '.').match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  function headerIndex(table, re) {
    const heads = [...table.querySelectorAll('thead th, tr:first-child th')];
    return heads.findIndex((th) => re.test(th.textContent));
  }

  function bodyRows(table) {
    return [...table.querySelectorAll('tbody tr')].filter((tr) => tr.querySelector('td'));
  }

  // Raw meat pink -> cooked brown, light enough to stay calm.
  function tempColor(t) {
    const k = Math.min(Math.max((t - 40) / 35, 0), 1);
    const from = [244, 176, 176];
    const to = [196, 156, 120];
    return `rgb(${from.map((c, i) => Math.round(c + (to[i] - c) * k)).join(',')})`;
  }

  function temperatureScales(content) {
    content.querySelectorAll('table').forEach((table) => {
      const col = headerIndex(table, /temperatura/i);
      if (col < 0) return;
      const rows = bodyRows(table)
        .map((tr) => {
          const cells = tr.querySelectorAll('td');
          const t = cells[col] ? firstNumber(cells[col].textContent) : null;
          return t === null || t < 30 || t > 100 ? null : { tr, cells, t, label: cells[0].textContent.trim() };
        })
        .filter(Boolean);
      if (rows.length < 3) return;

      rows.forEach(({ cells, t }) => {
        const chip = document.createElement('span');
        chip.className = 'ac-chip';
        chip.style.background = tempColor(t);
        cells[col].prepend(chip);
      });

      const min = Math.min(...rows.map((r) => r.t)) - 2;
      const max = Math.max(...rows.map((r) => r.t)) + 2;
      const scale = document.createElement('div');
      scale.className = 'ac-scale';
      scale.setAttribute('aria-hidden', 'true');
      const track = document.createElement('div');
      track.className = 'ac-scale__track';
      track.style.background = `linear-gradient(90deg, ${tempColor(min)}, ${tempColor(max)})`;
      scale.appendChild(track);
      rows.forEach(({ t, label }, i) => {
        const mark = document.createElement('div');
        mark.className = `ac-scale__mark${i % 2 ? ' ac-scale__mark--low' : ''}`;
        mark.style.left = `${((t - min) / (max - min)) * 100}%`;
        mark.innerHTML = `<b>${String(t).replace('.', ',')}°C</b><span></span>`;
        mark.lastChild.textContent = label.replace(/\s*\(.*?\)\s*/g, ' ').replace(/polecane$/, '').trim();
        scale.appendChild(mark);
      });
      table.before(scale);
    });
  }

  // Order matters: "tygodnie" contains "dni", so weeks are matched first.
  const UNIT_DAYS = [
    [/godzin/, 1 / 24],
    [/tydz|tygod/, 7],
    [/miesi/, 30],
    [/rok|lat/, 365],
    [/dni|dzień|dzien/, 1],
  ];

  function toDays(text) {
    const t = text.toLowerCase().replace(',', '.');
    const nums = t.match(/\d+(\.\d+)?/g);
    const unit = UNIT_DAYS.find(([re]) => re.test(t));
    if (!nums || !unit) return null;
    return parseFloat(nums[nums.length - 1]) * unit[1];
  }

  function storageBars(content) {
    content.querySelectorAll('table').forEach((table) => {
      const a = headerIndex(table, /tradycyjnie/i);
      const b = headerIndex(table, /próżniowo/i);
      if (a < 0 || b < 0) return;
      const rows = bodyRows(table)
        .map((tr) => {
          const cells = tr.querySelectorAll('td');
          return { cells, da: toDays(cells[a]?.textContent || ''), db: toDays(cells[b]?.textContent || '') };
        })
        .filter((r) => r.da && r.db);
      if (!rows.length) return;

      const top = Math.log10(Math.max(...rows.map((r) => Math.max(r.da, r.db))) + 1);
      table.classList.add('ac-bars');
      rows.forEach(({ cells, da, db }) => {
        [[cells[a], da, 'a'], [cells[b], db, 'b']].forEach(([cell, d, kind]) => {
          const bar = document.createElement('span');
          bar.className = `ac-bar ac-bar--${kind}`;
          bar.style.width = `${Math.max(6, (Math.log10(d + 1) / top) * 100)}%`;
          bar.setAttribute('aria-hidden', 'true');
          cell.appendChild(bar);
        });
        const times = Math.round(db / da);
        if (times >= 2) {
          const tag = document.createElement('span');
          tag.className = 'ac-times';
          tag.textContent = `ok. ${times}× dłużej`;
          cells[b].insertBefore(tag, cells[b].querySelector('.ac-bar'));
        }
      });
    });
  }

  function faq(content) {
    const h2 = [...content.querySelectorAll(':scope > h2')].find((h) => /najczę(ściej|stsze).*pytania/i.test(h.textContent));
    if (!h2) return;
    let el = h2.nextElementSibling;
    while (el && el.tagName !== 'H2') {
      const next = el.nextElementSibling;
      if (el.tagName === 'H3') {
        const details = document.createElement('details');
        details.className = 'ac-faq';
        const summary = document.createElement('summary');
        summary.textContent = el.textContent;
        details.appendChild(summary);
        let body = next;
        while (body && !/^H[23]$/.test(body.tagName) && !body.classList.contains('product-slot')) {
          const after = body.nextElementSibling;
          details.appendChild(body);
          body = after;
        }
        el.replaceWith(details);
        el = body;
        continue;
      }
      el = next;
    }
  }

  // Ingredient lists become a checklist the reader can tick while shopping or cooking.
  function ingredients(content) {
    content.querySelectorAll(':scope > h2, :scope > h3').forEach((h) => {
      if (!/^składniki/i.test(h.textContent.trim())) return;
      let list = h.nextElementSibling;
      while (list && list.tagName === 'P') list = list.nextElementSibling;
      if (!list || list.tagName !== 'UL') return;
      list.classList.add('ac-check');
      list.querySelectorAll(':scope > li').forEach((li) => {
        li.setAttribute('role', 'checkbox');
        li.setAttribute('aria-checked', 'false');
        li.tabIndex = 0;
        const toggle = () => li.setAttribute('aria-checked', li.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
        li.addEventListener('click', toggle);
        li.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
          }
        });
      });
    });
  }

  // <div data-infographic="NAME" data-temp=".." data-time=".." data-finish=".."></div> in the body
  // is replaced by the drawing main-article rendered into <template data-infographic-src="NAME">.
  function infographics(content) {
    content.querySelectorAll('[data-infographic]').forEach((slot) => {
      const tpl = document.querySelector(`template[data-infographic-src="${slot.dataset.infographic}"]`);
      if (!tpl) return;
      const figure = tpl.content.firstElementChild.cloneNode(true);
      ['temp', 'time', 'finish', 'pack'].forEach((key) => {
        const value = slot.dataset[key];
        const target = figure.querySelector(`[data-field="${key}"]`);
        if (value && target) target.textContent = value;
      });
      slot.replaceWith(figure);
    });
  }

  // <div data-infographic="kalkulator-przechowywania"></div>: a picker built from the
  // "Tradycyjnie / Próżniowo" tables already in the article, so the numbers always match the text.
  function storageCalculator(content) {
    const slot = content.querySelector('[data-infographic="kalkulator-przechowywania"]');
    if (!slot) return;
    const places = [];
    content.querySelectorAll('table').forEach((table) => {
      const a = headerIndex(table, /tradycyjnie/i);
      const b = headerIndex(table, /próżniowo/i);
      if (a < 0 || b < 0) return;
      let head = table.previousElementSibling;
      while (head && head.tagName !== 'H2') head = head.previousElementSibling;
      const name = head ? head.textContent.split(/[:—]/).pop().replace(/\(.*?\)/, '').trim() : 'Przechowywanie';
      const items = bodyRows(table).map((tr) => {
        const cells = tr.querySelectorAll('td');
        return {
          name: cells[0].textContent.trim(),
          a: cells[a].firstChild ? cells[a].firstChild.textContent.trim() : cells[a].textContent.trim(),
          b: cells[b].firstChild ? cells[b].firstChild.textContent.trim() : cells[b].textContent.trim(),
        };
      });
      if (items.length) places.push({ name: name.charAt(0).toUpperCase() + name.slice(1), items });
    });
    if (!places.length) return;

    const box = document.createElement('figure');
    box.className = 'ig ac-calc';
    box.id = 'kalkulator';
    box.innerHTML = `
      <p class="ac-calc__title">Sprawdź swój produkt</p>
      <div class="ac-calc__tabs" role="tablist" aria-label="Gdzie przechowujesz"></div>
      <div class="ac-calc__items" role="group" aria-label="Produkt"></div>
      <div class="ac-calc__result" aria-live="polite">
        <p class="ac-calc__big"></p>
        <p class="ac-calc__was"></p>
        <div class="ac-calc__bars">
          <span>Bez próżni</span><span class="ac-calc__track"><i class="ac-bar ac-bar--a"></i></span>
          <span>W próżni</span><span class="ac-calc__track"><i class="ac-bar ac-bar--b"></i></span>
        </div>
      </div>
      <figcaption>Czasy orientacyjne dla świeżych produktów w szczelnie zgrzanym worku. Dane z tabel w tym artykule.</figcaption>`;
    const tabs = box.querySelector('.ac-calc__tabs');
    const list = box.querySelector('.ac-calc__items');
    let place = 0;
    let item = 0;

    function render() {
      tabs.querySelectorAll('button').forEach((btn, i) => btn.setAttribute('aria-selected', String(i === place)));
      list.innerHTML = '';
      places[place].items.forEach((it, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ac-calc__item';
        btn.textContent = it.name;
        btn.setAttribute('aria-pressed', String(i === item));
        btn.addEventListener('click', () => { item = i; render(); });
        list.appendChild(btn);
      });
      const it = places[place].items[item];
      const da = toDays(it.a);
      const db = toDays(it.b);
      box.querySelector('.ac-calc__big').textContent = `W próżni: ${it.b}`;
      const times = da && db ? Math.round(db / da) : 0;
      box.querySelector('.ac-calc__was').textContent = `Bez próżni: ${it.a}${times >= 2 ? ` · ok. ${times}× dłużej` : ''}`;
      if (da && db) {
        box.querySelector('.ac-bar--a').style.width = `${Math.max(4, (da / db) * 100)}%`;
        box.querySelector('.ac-bar--b').style.width = '100%';
      }
    }

    places.forEach((p, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'tab');
      btn.textContent = p.name;
      btn.addEventListener('click', () => { place = i; item = 0; render(); });
      tabs.appendChild(btn);
    });
    render();
    slot.replaceWith(box);
  }

  function init() {
    const content = document.querySelector('.article-template__content');
    if (!content) return;
    [storageCalculator, infographics, notes, iconLists, ingredients, steps, recommendedRows, temperatureScales, storageBars, faq].forEach((fn) => {
      try {
        fn(content);
      } catch (e) {
        console.warn('article-enhance', fn.name, e);
      }
    });
    content.classList.add('ac-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
